import { useAuth } from './auth/AuthProvider'
import { useModules } from './api/queries'

/**
 * Menupunkterne ét sted.
 *
 * Sidemenuen på desktop (`AppShell`) og "Mere"-siden på mobil (`MorePage`)
 * havde hver sin liste, og mobilen løb bagud: syv adminsider - heriblandt
 * Moduler og Drift - kunne slet ikke nås fra en telefon, og de punkter der var
 * med, tog hverken hensyn til slukkede moduler (beslutning 048) eller til at
 * Microsoft 365 og SMS kun er for Superadmin.
 *
 * Derfor står listerne her, og begge menuer læser fra dem. En ny adminside
 * skrives ét sted og dukker op begge steder.
 */
export interface NavItem {
  to: string
  label: string
  icon: string
  /** Vises kun for Superadmin. */
  superadminOnly?: boolean
  /**
   * Vises også for projektledere. Resten af administrationsmenuen kræver
   * Administrator - men en projektleder skal kunne sende en besked ud til sine
   * folk uden at have adgang til alt det andet (beslutning 042).
   */
  projectManager?: boolean

  /** Kun for dem, der må have VPN - ikke lærlinge (beslutning 108). */
  vpnTechnician?: boolean

  /**
   * Modulet punktet hoerer til (beslutning 048). Er modulet slaaet fra,
   * forsvinder punktet fra menuen. Uden det ville folk klikke sig ind paa en
   * side, der bare svarer "modulet er slaaet fra".
   */
  module?: string

  /** Kun for kontoret: Kontor og administratorer (`isIntranetAdmin`). */
  office?: boolean
}

export const mainNav: NavItem[] = [
  { to: '/', label: 'Hjem', icon: '🏠' },
  // Husets egen side (beslutning 100): opslag, begivenheder, telefonliste og genveje.
  { to: '/intranet', label: 'Intranet', icon: '📣', module: 'intranet' },
  // Markens egen genvej (beslutning 082). Staar hoejt, fordi den bruges med
  // handsker paa og en kunde der venter.
  { to: '/quick-note', label: 'Hurtig note', icon: '📝' },
  // Kvitteringer for mad paa lange dage (beslutning 114).
  { to: '/receipts', label: 'Kvitteringer', icon: '🧾', module: 'receipts' },
  { to: '/calendar', label: 'Kalender', icon: '📅' },
  { to: '/map', label: 'Kort', icon: '🗺️', module: 'map' },
  { to: '/projects', label: 'Sager', icon: '📁' },
  // Diagrammer paa sagen (beslutning 150) - kun Projektleder og opefter.
  { to: '/diagrams', label: 'Diagrammer', icon: '📐', projectManager: true },
  // Sager på 100 %, der skal faktureres (beslutning 147) - kun kontoret.
  { to: '/invoicing', label: 'Til fakturering', icon: '💰', office: true },
  // "Opgaver" er taget ud af menuen (Simon 2026-10-28): opgaverne findes på sagen,
  // og der skal man gå ind. Siden /tasks findes stadig, så gamle links virker.
  { to: '/customers', label: 'Kunder', icon: '🏢' },
  { to: '/devices', label: 'Enheder', icon: '🔋', module: 'service' },
  { to: '/scan', label: 'Service', icon: '📷', module: 'service' },
  { to: '/instruments', label: 'Måleinstrumenter', icon: '📏', module: 'instruments' },
  { to: '/absences', label: 'Fravær', icon: '🌴', module: 'absence' },
  { to: '/certificates', label: 'Certifikater', icon: '🎓', module: 'certificates' },
  { to: '/access-keys', label: 'Nøgler', icon: '🔑', module: 'keys' },
  { to: '/equipment', label: 'Værktøj', icon: '🧰', module: 'equipment' },
  // Firmabiler (beslutning 123).
  { to: '/vehicles', label: 'Biler', icon: '🚗', module: 'vehicles' },
  { to: '/warranties', label: 'Garantier', icon: '🛡️', module: 'warranties' },
  // Markens side staar FOER varekartoteket: den, der aabner lageret paa en
  // telefon, staar som regel ved bagklappen og skal scanne - ikke slaa op.
  { to: '/stock/scan', label: 'Scan ind og ud', icon: '📲', module: 'stock' },
  // Varer til bilen (beslutning 127) - montoeren bestiller, lageret pakker.
  { to: '/stock/orders', label: 'Varer til bilen', icon: '🛻', module: 'stock_van_orders' },
  { to: '/stock/items', label: 'Varer', icon: '📦', module: 'stock' },
  { to: '/stock/locations', label: 'Lagre og biler', icon: '🚚', module: 'stock' },
  { to: '/support', label: 'Henvendelser', icon: '📨', module: 'support' },
  { to: '/manuals', label: 'Manualer', icon: '📖', module: 'manuals' },
  // Montørens egen VPN-profil (beslutning 091). Adgang til et anlæg gives af Superadmin.
  { to: '/vpn', label: 'VPN-adgang', icon: '🔐', module: 'vpn', vpnTechnician: true },
  { to: '/notifications', label: 'Notifikationer', icon: '🔔' },
  // Vejledningen (beslutning 089) - viser kun det, brugerens rolle kan.
  { to: '/guide', label: 'Vejledning', icon: '❓' }
]

export const adminNav: NavItem[] = [
  { to: '/admin/users', label: 'Brugere', icon: '👥' },
  { to: '/admin/teams', label: 'Teams', icon: '👷' },
  // Projektleder og opefter retter procedurer og skabeloner (beslutning 141).
  { to: '/admin/templates', label: 'Procedurer', icon: '📋', projectManager: true },
  { to: '/admin/service-checklists', label: 'Servicechecklister', icon: '🔧', module: 'service' },
  { to: '/admin/manual-categories', label: 'Manualhylder', icon: '🗂️', module: 'manuals' },
  // Faste typer og modeller paa enhederne (beslutning 105).
  { to: '/admin/device-types', label: 'Enhedstyper', icon: '🏷️', module: 'service' },
  // Hvad slags vaerktoej det er (beslutning 096). Fast liste, saa der ikke
  // opstaar to stavemaader af det samme.
  { to: '/admin/equipment-categories', label: 'Værktøjskategorier', icon: '🧰', module: 'equipment' },
  { to: '/admin/statuses', label: 'Statusser', icon: '🎨' },
  { to: '/admin/screens', label: 'TV-skærme', icon: '📺', module: 'screens' },
  { to: '/admin/dashboards', label: 'Dashboards', icon: '📊' },
  { to: '/admin/activity', label: 'Aktivitetslog', icon: '📝' },
  { to: '/admin/deadline-alerts', label: 'Deadline-varsler', icon: '⏰' },
  { to: '/admin/messages', label: 'Beskeder', icon: '📣', projectManager: true },
  { to: '/field-messages', label: 'Akut og udkald', icon: '🚨', projectManager: true, module: 'field_messages' },
  { to: '/admin/support-mail', label: 'Support-mail', icon: '📨', module: 'support' },
  { to: '/admin/price-import', label: 'Prisfiler', icon: '🏷️', module: 'stock' },
  { to: '/admin/subscriptions', label: 'Abonnementer', icon: '💳', module: 'subscriptions' },
  { to: '/admin/modules', label: 'Moduler', icon: '🧩' },
  { to: '/admin/operations', label: 'Drift', icon: '🛠️' },
  { to: '/admin/microsoft365', label: 'Microsoft 365', icon: '🔗', superadminOnly: true },
  { to: '/admin/sms', label: 'SMS', icon: '💬', superadminOnly: true },
  // Nøglen til Ordrestyrings API (beslutning 137).
  { to: '/admin/ordrestyring', label: 'Ordrestyring', icon: '🧾', superadminOnly: true },
  { to: '/admin/vpn', label: 'VPN', icon: '🔐', superadminOnly: true, module: 'vpn' }
]

export const bottomNav: NavItem[] = [
  { to: '/', label: 'Hjem', icon: '🏠' },
  // Husets egen side staar lige ved siden af Hjem (beslutning 103): det er dér,
  // opslag, fester og de interne opgaver dukker op, og tallet skal kunne ses.
  { to: '/intranet', label: 'Intranet', icon: '📣', module: 'intranet' },
  // Markens egen genvej (beslutning 082). Den staar i bundmenuen og ikke kun paa
  // forsiden, fordi det, der skal huskes, lige saa tit falder ind, mens man staar
  // inde paa en sag. Kort navn: seks punkter skal kunne staa paa én linje hver.
  { to: '/quick-note', label: 'Note', icon: '📝' },
  { to: '/calendar', label: 'Kalender', icon: '📅' },
  // Samme ord som sidemenuen - det hed "Projekter" her og "Sager" dér.
  { to: '/projects', label: 'Sager', icon: '📁' },
  { to: '/scan', label: 'Service', icon: '📷', module: 'service' },
  { to: '/more', label: 'Mere', icon: '☰' }
]

export const pageTitles: Record<string, string> = {
  '/': 'Mit overblik',
  '/quick-note': 'Hurtig note',
  '/receipts': 'Kvitteringer',
  '/invoicing': 'Til fakturering',
  '/calendar': 'Kalender',
  '/map': 'Kort',
  '/instruments': 'Måleinstrumenter',
  '/absences': 'Fravær og ferie',
  '/certificates': 'Certifikater og kurser',
  '/access-keys': 'Nøgler og adgangsbrikker',
  '/equipment': 'Værktøj',
  '/vehicles': 'Firmabiler',
  '/admin/equipment-categories': 'Værktøjskategorier',
  '/admin/device-types': 'Enhedstyper',
  '/warranties': 'Garantier og reklamationer',
  '/admin/modules': 'Moduler',
  '/intranet': 'Intranet',
  '/admin/operations': 'Drift',
  '/projects': 'Sager',
  '/customers': 'Kunder',
  '/devices': 'Enheder',
  '/scan': 'Scan',
  '/stock/scan': 'Scan ind og ud',
  '/stock/orders': 'Varer til bilen',
  '/stock/items': 'Varer',
  '/stock/locations': 'Lagre og biler',
  '/admin/templates': 'Procedureskabeloner',
  '/diagrams': 'Diagrammer',
  '/admin/service-checklists': 'Servicechecklister',
  '/tasks': 'Opgaver',
  '/notifications': 'Notifikationer',
  '/guide': 'Vejledning',
  '/more': 'Mere',
  '/admin/users': 'Brugere',
  '/admin/statuses': 'Statusser og prioriteter',
  '/admin/screens': 'TV-skærme',
  '/admin/dashboards': 'Dashboards',
  '/admin/activity': 'Aktivitetslog',
  '/admin/deadline-alerts': 'Deadline-varsler',
  '/admin/messages': 'Beskeder',
  '/field-messages': 'Akut service og beskeder',
  '/support': 'Henvendelser',
  '/admin/support-mail': 'Mail ind i indbakken',
  '/admin/price-import': 'Prisfiler',
  '/admin/subscriptions': 'Abonnementer og fakturering',
  '/admin/microsoft365': 'Microsoft 365',
  '/admin/sms': 'SMS',
  '/admin/ordrestyring': 'Ordrestyring',
  '/admin/vpn': 'VPN til kundeanlæg',
  '/vpn': 'VPN-adgang'
}

/**
 * Er modulet bag et menupunkt tændt?
 *
 * Er listen endnu ikke hentet, vises alt: hellere et punkt for meget et kort
 * oejeblik end en menu, der hopper, hver gang siden aabnes.
 */
export function useModuleEnabled() {
  const { data: modules } = useModules()
  return (key?: string) =>
    key === undefined || !modules || (modules.find((m) => m.key === key)?.enabled ?? true)
}

/** Hovedmenuens punkter, med de slukkede moduler pillet ud. */
export function useVisibleMainNav(): NavItem[] {
  const moduleEnabled = useModuleEnabled()
  const { canUseVpn, isIntranetAdmin } = useAuth()
  return mainNav.filter(
    (item) =>
      moduleEnabled(item.module) && (!item.vpnTechnician || canUseVpn) && (!item.office || isIntranetAdmin)
  )
}

/**
 * Administrationsmenuens punkter for den, der er logget ind.
 *
 * Tom liste betyder, at afsnittet slet ikke skal vises. En projektleder ser
 * kun de punkter, der udtrykkeligt er sat til det.
 */
export function useVisibleAdminNav(): NavItem[] {
  const { isAdmin, isProjectManager, hasRole } = useAuth()
  const moduleEnabled = useModuleEnabled()

  if (!isAdmin && !isProjectManager) return []

  return adminNav
    .filter((item) => (isAdmin ? true : item.projectManager === true))
    .filter((item) => !item.superadminOnly || hasRole('Superadmin'))
    .filter((item) => moduleEnabled(item.module))
}

/** Bundmenuens punkter, med de slukkede moduler pillet ud. */
export function useVisibleBottomNav(): NavItem[] {
  const moduleEnabled = useModuleEnabled()
  return bottomNav.filter((item) => moduleEnabled(item.module))
}

/**
 * Hovedmenuens punkter til "Mere"-siden: dem, der ikke allerede står i
 * bundmenuen.
 *
 * Sidemenuen er skjult under 900 px, så uden denne liste kunne femten sider -
 * Opgaver, Kunder, Varer og resten - slet ikke nås fra en telefon.
 */
export function useVisibleMoreNav(): NavItem[] {
  const main = useVisibleMainNav()
  const bottom = new Set(bottomNav.map((item) => item.to))
  return main.filter((item) => !bottom.has(item.to))
}
